/**
 * The game.
 *
 * The whole thing runs here, in the tab. Every hand, every bot decision, every Monte
 * Carlo trial. Nothing is asked of the server while you play — not a request per
 * action, not a row per hand — and the session posts a single summary at the end.
 *
 * That is a deliberate consequence of what this project learned the expensive way: an
 * API that writes on every interaction is an API with a daily limit, and a poker game
 * is thousands of interactions an hour. Running it locally also means it is instant,
 * works on a plane, and costs nothing per player, which is what makes a free tier
 * possible at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Game, type GameConfig } from "@engine/game";
import type { Action } from "@engine/types";
import type { TableState } from "@engine/table-state";
import { tableOf } from "@engine/bot/profile";
import { derive, findings, type Finding } from "@engine/stats";
import { TopDownTable } from "../table/TopDownTable";
import { SaloonTable, type SaloonTableProps } from "../table/SaloonTable";
import { useTableView } from "../settings/view";
import { ViewToggle } from "../settings/ViewToggle";
import { Director, type DirectorSnapshot } from "../table/director";
import { actionLabel } from "../table/display";
import { GhostActionBar } from "../table/GhostActionBar";
import { useScene } from "../table/useScene";
import { payoffLine, decodeScore, type Payoff } from "../table/payoff";
import { evaluate7 } from "@pipeline/solve/evaluator";
import { preloadTable } from "../table/preload";
import { FlightBus } from "../table/flight";
import { getPace, onPaceChange, prefersReducedMotion } from "../table/settings";
import { Voice, COOLDOWN } from "../table/voice";
import { useVoice } from "../table/useVoice";
import type { ArchetypeId } from "../table/lines";
import { TauntTray, ShowControl } from "../table/TauntTray";
import { cueForStep, play as playCue, unlock as unlockSound, setMuted, setVolume, close as closeSound } from "../table/sounds";
import { durationsFor } from "../table/pace";

/*
 * PHASE 9: THE CLASSIC FELT IS RETIRED. `VITE_TABLE_VIEW=classic` rendered `GameTable`,
 * kept as the way back "if the new table turns out to be wrong on a real device". It has
 * been played on since Phase 5, on phones and on the desktop, and the guard tests that
 * read the old felt as the definition of one deck and one felt now read the kit tables
 * (PHASE9-REPORT §4 has the migration, assertion by assertion). The flag, the component
 * and their CSS are gone; the table is `TopDownTable` or `SaloonTable`, by preference.
 */
import { ActionBar } from "../components/ActionBar";
import { seedFor, sizingUnit } from "../lib/table-game";
import { MoneyProvider, MoneyToggle, useMoney } from "../lib/MoneyContext";
import type { Setup } from "../lib/setup";
import { RoomHeader } from "../rooms/RoomShell";
import { roomOf } from "../rooms/rooms";
import type { Resolved } from "../rooms/seat-url";
import { getPrefs, onPrefsChange, reducedMotionOverride, getPersona } from "../settings/prefs";
import { SettingsSheet } from "../settings/SettingsSheet";
import { personaAvatar } from "../settings/PersonaPicker";
import { castFaces, seatAvatar } from "../art/casting";
import { api, ApiError } from "../lib/api";
import { useWallet } from "../lib/WalletContext";
import { Barkeep } from "../rooms/Barkeep";
import { seatCost, settleAmount, startChipsFor, REBUY_TAKES_A_SECOND_BUYIN } from "../lib/wallet-client";
import { HandHistory } from "../components/HandHistory";
import { StatsPanel } from "../components/StatsPanel";
import { SeatFinder } from "../rooms/SeatFinder";
import { Ledger, type LedgerCastRow, type LedgerProps } from "../rooms/Ledger";
import {
  verdictLine, seatHook, spotLabel, LEDGER_COPY, type SittingSummary, type Verdict,
} from "../rooms/copy";
import { avatarUrlForName } from "../art/roster";
import { positionOf } from "../lib/table-game";
import { getLastVerdict, setLastVerdict, getLastSitting, setLastSitting } from "../settings/prefs";
import { prizePool, stakesLabel } from "../lib/wallet-client";
import { BucksAmount } from "../lib/WalletContext";
import type { Street } from "@engine/types";

/**
 * ===================== PHASE 2B: THE TABLE LIVES IN A ROOM =====================
 * This route used to be two screens: a setup FORM and, once you had answered eleven
 * questions, a table. The form is gone — every field of it is now on a card, on Custom
 * Table or in the Settings sheet — and this component is only ever the table. It is
 * handed the `Setup` the card resolved (`rooms/seat-url.ts` reads it off the URL, so a
 * seat can be linked to and a reload keeps its table) and it sits down immediately.
 *
 * The page chrome went with the form. There is no `<h1>Table</h1>`, no subtitle, no
 * stack/units/leave row and no `.td-embed` break-out: the ROOM HEADER carries Leave and
 * the units toggle, and the felt and its action band have the rest of the screen. On a
 * 390x844 phone that is the difference between the action bar being on screen and being
 * 265px below it.
 * ===============================================================================
 */
/** The pace, with the OS's reduced-motion setting and the player's own override. */
function motionNow() {
  return durationsFor(getPace(), prefersReducedMotion() || reducedMotionOverride());
}

/** The room this route is in. One lookup, at module scope; `ROOMS` is the source. */
const POKER = roomOf("poker")!;

export function Play({ seat }: { seat: Resolved }) {
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [setup, setSetup] = useState<Setup>(seat.setup);
  const [game, setGame] = useState<Game | null>(null);
  /*
   * Phase 3: the table is drawn from the Director's snapshot, never from the engine.
   *
   * `startHand()` and `act()` run every bot to the next decision in one call, so the
   * engine is always AHEAD of what the player has been shown. The Director plays the
   * event stream back at pace and hands the presented table over through `onChange`;
   * `snap.state` is what both felts render, and `snap.phase` is what the controls key
   * on. `game` is still here for everything that is not the felt — history, stats,
   * the reveal, the legal actions when it is the hero's turn.
   */
  const [snap, setSnap] = useState<DirectorSnapshot | null>(null);
  const director = useRef<Director | null>(null);
  const [flights] = useState(() => new FlightBus());
  /*
   * Phase 6: the table's voice, rebuilt with every `Game`.
   *
   * State and not a ref, deliberately: `useVoice` subscribes to it, and a ref changing
   * is not a render, so a hook holding a ref would go on listening to the voice of the
   * sitting before this one.
   */
  const [voice, setVoice] = useState<Voice | null>(null);
  /* Table talk, re-read whenever the sheet writes. Off puts the tray away entirely. */
  const [talkOn, setTalkOn] = useState(() => getPrefs().taunts);
  /* What the tray's button draws: milliseconds left of the twenty-second cooldown. */
  const [trayLeft, setTrayLeft] = useState(0);
  /* The hero's two cards while Show is on offer, and null the rest of the time. */
  const [showOffer, setShowOffer] = useState<readonly number[] | null>(null);
  /*
   * The timeline, from the pace AND the player's own reduced-motion answer.
   *
   * `currentDurations()` reads the OPERATING SYSTEM's setting, which is right and is not
   * the whole story: the Settings sheet offers "Less motion" to somebody whose machine
   * says nothing, and a switch that stored an answer nobody read would be decoration.
   * The two are ORed here rather than inside `table/settings.ts`, which stays free of
   * anything the Director's Node tests cannot import.
   */
  const [motion, setMotion] = useState(motionNow);
  /* The player's own face, re-read whenever the Settings sheet writes. */
  const [persona, setPersona] = useState<string | null>(() => getPersona());
  /* Phase 3b: the deck and the payoff, read off the steps and the presented table. */
  const scene = useScene(flights, snap, motion);
  /*
   * Phase 6: the bubbles, the faces and the shown hands, read off the same steps.
   *
   * `voiceTick` is bumped when a new `Game` is built, which is what re-subscribes the
   * hook to the new `Voice` — the ref alone would not, because a ref change is not a
   * render.
   */
  const said = useVoice(voice, flights, motion);
  /*
   * Phase 7: which projection of the felt — the saloon on a wide screen unless the
   * player chose otherwise, the top-down table everywhere else (`settings/view.ts`).
   * Both take the same props; the saloon also takes the persona's slug, to stand the
   * player up. Nothing else on this route changes with it — the seat card, the Ledger
   * and the action bar are the same in both.
   */
  const tableView = useTableView();
  const Felt: React.ComponentType<SaloonTableProps> = tableView.view === "saloon" ? SaloonTable : TopDownTable;
  const [, forceRender] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /**
   * ==================== PHASE 5: THE ROUTE IS THREE SCREENS ====================
   * `seat` — Finding a seat. The card that buys the seat, shows the commitment and
   *          seats the cast. Every sit-down starts here, in every room.
   * `table` — the felt.
   * `ledger` — the end of the sitting. A SCREEN, not the overlay the reveal panel was:
   *          the felt is gone behind it, because the sitting is over.
   *
   * `revealing` is what this replaces. It was a boolean that laid a panel over a live
   * table — and the table really was live behind it, which is the bug the `standing`
   * ref was added to paper over.
   * ===============================================================================
   */
  const [screen, setScreen] = useState<"seat" | "table" | "ledger">("seat");
  /**
   * Which sitting at this seat. Bumped by "Play again", which is what re-opens the seat
   * card, re-buys the seat and re-deals — the same card, a new receipt, a new shuffle.
   */
  const [sitting, setSitting] = useState(0);
  /**
   * Box 4: the hero is out of chips at a cash table, and the felt is paused.
   *
   * Declared with the other screen state rather than beside the effect that sets it,
   * because the auto-deal effect above reads it — a cash table that kept dealing behind
   * a rebuy card would be dealing the player back in with chips they had not bought.
   */
  const [rebuy, setRebuy] = useState(false);
  const [rebuyShort, setRebuyShort] = useState<{ cost: number; balance: number } | null>(null);
  /* Level 3 now owns auto-deal: the checkbox under the felt became a line in the
     Settings sheet, and this keeps in step with it wherever it is changed. */
  const [autoDeal, setAutoDeal] = useState(() => getPrefs().autoDeal);
  const timer = useRef<number | null>(null);
  const startedAt = useRef(Date.now());
  const saved = useRef(false);
  /** The player pressed Leave. A ref, so `deal` can see it without being rebuilt. */
  const standing = useRef(false);
  const { getToken, isSignedIn } = useAuth();
  const [saveNote, setSaveNote] = useState<string | null>(null);

  /* ---------------------------------------------------------- Buster Bucks --- */

  /**
   * THE SEAT IS BOUGHT BEFORE THE FIRST HAND IS DEALT.
   *
   * `api.seatReceipt` is the debit: the server takes the buy-in out of the wallet and
   * hands back a signed ticket, and the settlement at Leave has to present it. There is
   * no way to be seated without the balance having moved first, which is the whole
   * point of doing it server-side — the client cannot be trusted to say what it paid.
   *
   * A signed-out player buys the same seat from the device wallet in
   * `lib/wallet-client.ts`, running the same rules locally, and settles locally too.
   * Everything below this line is the same code either way.
   */
  const wallet = useWallet();
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  /**
   * `getToken` in a ref, and NOT in `buySeat`'s dependencies.
   *
   * Clerk's `getToken` is a new closure on every render. With it in the dependency list
   * `buySeat` was new every render, so the sit-down effect re-ran every render — and
   * every run of it bought another seat. Caught in the harness by the console: eleven
   * 402s on one page load, a wallet drained from 5,000 to nothing before the first card
   * was dealt, and a table that dealt anyway because the eleventh failure was not the
   * one that decided.
   *
   * This is the third time this exact trap has been sprung in this file — `useMe` has
   * the note, `saveSession`'s flush effect has the note — and it is why both of those
   * read everything they need off a ref.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  /** The ticket for THIS sitting. Null means it is unfunded and will settle nothing. */
  const ticket = useRef<{ receipt: string | null; buyin: number } | null>(null);
  const [barkeep, setBarkeep] = useState(false);
  /** How much this sitting has paid in, across the buy-in and any rebuys. */
  const paid = useRef(0);
  /** A purchase is in flight. Set before the request, so two callers cannot race it. */
  const buying = useRef(false);
  /** The settlement is in flight. The Ledger says so rather than printing a zero. */
  const [settling, setSettling] = useState(false);
  /** What the settlement gave back, once it has. Null while the sitting is live. */
  const [settled, setSettled] = useState<{ paid: number; credited: number | null } | null>(null);

  /**
   * The price of this seat, in Bucks.
   *
   * A CASH seat costs its starting stack, because at a cash table a chip is a Buck. A
   * TOURNAMENT seat costs what it says: `card.buyin` on a named card, and — Phase 5 box
   * 5 — `setup.buyin` on a Custom one, which used to name nothing and therefore cost
   * nothing. `seatCost` does the arithmetic; nothing here repeats it.
   */
  const costOf = useCallback((s: Setup) => seatCost({
    format: s.format, seats: s.seats, bigBlind: s.bigBlind, stackBb: s.stackBb,
    buyin: seat.card?.buyin ?? s.buyin,
  }), [seat.card]);

  /**
   * Buy one seat, or one rebuy, and say plainly which happened.
   *
   * A REBUY presents the ticket this sitting already holds, and the server answers with
   * a replacement carrying the cumulative total on the SAME nonce — so a sitting still
   * settles exactly once however many times the player bought back in. That is Phase
   * 4's shape and the blackjack room has used it since; this is the poker table
   * learning it, because a cash table that keeps its stacks (box 4) is the first poker
   * table where busting is a thing that can happen to the hero.
   *
   * The return value is what the seat card and the rebuy card both key on: `ok`, or the
   * two numbers a player needs to see when they cannot afford it.
   */
  const purchase = useCallback(async (
    s: Setup, opts: { rebuy?: boolean } = {},
  ): Promise<{ ok: true } | { ok: false; cost: number; balance: number }> => {
    const cost = costOf(s);
    /* A table that costs nothing is a table with nothing to settle — a drill-shaped
       seat, or a card with no price yet. It deals, and it touches no wallet. */
    if (cost <= 0) return { ok: true };
    /**
     * ONE SEAT PER SITTING, whatever calls this.
     *
     * The fourth time this trap has been laid in this file, and the first three are
     * written up in the notes above: an effect that buys a seat must be impossible to
     * run twice. `SeatFinder` already runs its own effect once per attempt, and React
     * still runs effects twice in StrictMode on purpose. Holding a ticket IS the record
     * that this sitting has been paid for, so it is what guards the door — and a 402
     * leaves no ticket, so a retry after the Barkeep is let through.
     *
     * A REBUY says so explicitly and is the one caller allowed past, because buying
     * back in is exactly a second posting on a sitting that already has a ticket.
     */
    if (!opts.rebuy && (ticket.current || buying.current)) return { ok: true };
    /*
     * The guard is set BEFORE the await, not after it.
     *
     * Holding a ticket is the record that a sitting has been paid for, and it arrives
     * one network round trip after the money leaves. Two calls that both got in during
     * that trip both read `null`, both post with no receipt, and the server sees two
     * fresh nonces: two debits, and one of the two tickets orphaned forever. React runs
     * an effect twice in StrictMode on purpose, so this is not a hypothetical.
     */
    buying.current = true;

    if (!isSignedIn) {
      if (!walletRef.current.spendLocal(cost)) {
        buying.current = false;
        return { ok: false, cost, balance: walletRef.current.balance };
      }
      ticket.current = { receipt: null, buyin: (ticket.current?.buyin ?? 0) + cost };
      paid.current += cost;
      buying.current = false;
      return { ok: true };
    }

    try {
      const r = await api.seatReceipt(tokenRef.current, {
        which: "poker", buyin: cost, seats: s.seats, bigBlind: s.bigBlind,
        /* Present, this is a rebuy on the sitting's own nonce. Absent, a fresh seat. */
        receipt: ticket.current?.receipt ?? undefined,
      });
      ticket.current = { receipt: r.receipt, buyin: r.buyin };
      paid.current += cost;
      walletRef.current.applied(r.balance, -cost);
      buying.current = false;
      return { ok: true };
    } catch (e) {
      /**
       * 402 is the honest refusal: not enough Bucks. The seat card says what the seat
       * costs and what you have, and the way out is the Barkeep — never a smaller seat
       * chosen on the player's behalf.
       */
      buying.current = false;
      if (e instanceof ApiError && e.status === 402) {
        const body = e.body as { balance?: number } | null;
        return { ok: false, cost, balance: Number(body?.balance ?? walletRef.current.balance) };
      }
      /*
       * Anything else — a dropped connection, a Worker deployed before Phase 4 — deals
       * the hand anyway with no ticket. The sitting is unfunded: it is stored as a
       * stats row and settles nothing, which is exactly what a session from before the
       * wallet existed does. A player who came here to practise gets to practise.
       */
      void walletRef.current.refresh();
      return { ok: true };
    }
    /* `seat.card` is a member of the frozen `PLAY_CARDS` list, so it is a stable
       reference; `isSignedIn` is a boolean that changes when something happened. */
  }, [isSignedIn, costOf]);

  const bump = useCallback(() => forceRender((n) => n + 1), []);

  /**
   * Post the session summary, once, when the player leaves the table.
   *
   * Deliberately best-effort: the game has already happened, the stats are already on
   * screen, and a failed request must not cost the player anything or block them
   * leaving. If it fails they are told, and nothing else changes.
   */
  const saveSession = useCallback(async (g: Game, s: Setup) => {
    if (saved.current) return;
    const st = g.stats[g.config.heroSeat];
    /**
     * A sitting with no completed hand still has to SETTLE.
     *
     * The guard used to be "no hands, nothing to do", and it was right about the stats
     * row: a session that produced no hand has no history worth writing. It is wrong
     * about the money now. Somebody who sits down, sees the table and stands up before
     * the first hand is over has bought a seat and played nothing, and taking their
     * buy-in for that is a bug — the credit is exactly what they paid.
     *
     * So the early return only applies when there is nothing to settle either. The
     * server derives the credit from `net`, which is zero here, and `bucksOut` returns
     * the buy-in unchanged.
     */
    if ((!st || st.hands === 0) && !ticket.current) return;
    saved.current = true;
    setSettling(true);

    /**
     * A signed-out player settles on this device and posts nothing.
     *
     * There is no account to write a stats row against, and there never was — what is
     * new is that there IS a wallet, kept in local storage, and it has to be credited
     * with what the sitting was worth or the guest's Bucks only ever go down. The
     * arithmetic is the server's, run locally, from the same function.
     */
    if (!isSignedIn) {
      const t = ticket.current;
      if (t) {
        const owed = settleAmount(t.buyin, startChipsFor(s, t.buyin), st?.net ?? 0, t.buyin * s.seats);
        if (owed !== null) walletRef.current.creditLocal(owed);
        /* What the Ledger prints. Derived from the same function the server uses, so
           a guest and an account see the same number for the same sitting. */
        setSettled({ paid: t.buyin, credited: owed });
      } else {
        setSettled(null);
      }
      ticket.current = null;
      setSettling(false);
      setSaveNote("Not saved — you are playing as a guest, so this session and your Bucks stayed on this device.");
      return;
    }

    /* Nothing was played and there is a seat to give back: settle it and post no row. */
    if (!st || st.hands === 0) {
      const t = ticket.current;
      if (t?.receipt) {
        try {
          const res = await api.saveGameSession(tokenRef.current, {
            startedAt: Math.floor(startedAt.current / 1000),
            endedAt: Math.floor(Date.now() / 1000),
            seats: s.seats, format: s.format, bigBlind: s.bigBlind, stackBb: s.stackBb,
            difficulty: s.difficulty, opponents: [],
            stats: {
              hands: 0, net: 0, vpip: 0, pfr: 0, threeBetChances: 0, threeBets: 0,
              facedThreeBet: 0, foldedToThreeBet: 0, sawFlop: 0, wentToShowdown: 0,
              wonAtShowdown: 0, wonWhenSawFlop: 0, aggressiveActions: 0, passiveActions: 0,
              cbetChances: 0, cbets: 0, facedCbet: 0, foldedToCbet: 0, allIns: 0,
              decisions: 0, evLost: 0,
            },
            evLostByStreet: { preflop: 0, flop: 0, turn: 0, river: 0 },
            receipt: t.receipt,
            stackOut: settleAmount(t.buyin, startChipsFor(s, t.buyin), 0, t.buyin * s.seats),
          });
          if (res.wallet?.ok && typeof res.wallet.balance === "number") {
            walletRef.current.applied(res.wallet.balance, res.wallet.credited ?? 0);
          }
          setSettled({ paid: t.buyin, credited: res.wallet?.credited ?? null });
        } catch { saved.current = false; }
      }
      ticket.current = null;
      setSettling(false);
      return;
    }

    try {
      const res = await api.saveGameSession(tokenRef.current, {
        startedAt: Math.floor(startedAt.current / 1000),
        endedAt: Math.floor(Date.now() / 1000),
        seats: s.seats,
        format: s.format,
        bigBlind: s.bigBlind,
        stackBb: s.stackBb,
        difficulty: s.difficulty,
        opponents: g.profiles.filter((p) => p !== null).map((p) => p!.id),
        stats: {
          hands: st.hands, net: st.net, vpip: st.vpip, pfr: st.pfr,
          threeBetChances: st.threeBetChances, threeBets: st.threeBets,
          facedThreeBet: st.facedThreeBet, foldedToThreeBet: st.foldedToThreeBet,
          sawFlop: st.sawFlop, wentToShowdown: st.wentToShowdown,
          wonAtShowdown: st.wonAtShowdown, wonWhenSawFlop: st.wonWhenSawFlop,
          aggressiveActions: st.aggressiveActions, passiveActions: st.passiveActions,
          cbetChances: st.cbetChances, cbets: st.cbets,
          facedCbet: st.facedCbet, foldedToCbet: st.foldedToCbet,
          allIns: st.allIns, decisions: st.decisions, evLost: st.evLost,
        },
        evLostByStreet: st.evLostByStreet,
        /*
         * The wallet half. `stackOut` is a CROSS-CHECK, not an instruction: the server
         * derives what the sitting was worth from the summary above and refuses a
         * settlement that disagrees with it. The formula is shared with the Worker and
         * a test runs both over random tables.
         */
        receipt: ticket.current?.receipt ?? undefined,
        stackOut: ticket.current
          ? settleAmount(ticket.current.buyin, startChipsFor(s, ticket.current.buyin), st.net, ticket.current.buyin * s.seats)
          : undefined,
      });
      /*
       * The header must not keep showing the old balance while the player is looking
       * at the reveal, so the settlement's own reply moves it — no second round trip.
       */
      if (res.wallet?.ok && typeof res.wallet.balance === "number") {
        walletRef.current.applied(res.wallet.balance, res.wallet.credited ?? 0);
      }
      setSettled(ticket.current
        ? { paid: ticket.current.buyin, credited: res.wallet?.credited ?? null }
        : null);
      setSettling(false);
      setSaveNote(res.stored
        ? `Saved ${st.hands} hands to your history.`
        : "Not saved — analytics are off in your account settings, so this session stayed on this device.");
    } catch {
      /**
       * Let it be retried. `saved` is set before the request so a double-click cannot
       * post twice; leaving it set after a FAILURE meant a session lost to a dropped
       * connection could never be recovered, and the message saying so was written
       * into state that the screen the player had just moved to does not render.
       */
      saved.current = false;
      /* Phase 5: and the Ledger is told the truth about it. A sitting that PAID and did
         not settle is not a sitting where no Bucks changed hands, so `credited: null`
         is a third state rather than a zero — the screen says it has not settled yet
         and leaving the table again will retry. */
      if (ticket.current) setSettled({ paid: ticket.current.buyin, credited: null });
      setSettling(false);
      setSaveNote("Could not save this session — it is still on this device. Leaving the table again will retry.");
    }
  }, [getToken, isSignedIn]);

  /**
   * The end of a sitting, whichever way it ends: Leave, the tournament running out of
   * players, or a busted hero declining the rebuy.
   *
   * One function so the three paths cannot drift. It stands the player up, settles,
   * writes the device record the Barkeep's line reads (box 6), and opens the Ledger.
   * The Ledger is a SCREEN — nothing is left running behind it, which is exactly what
   * the `standing` ref was invented to work around when the reveal was an overlay.
   */
  /*
   * `saveSession` off a ref, for the fourth time in this file.
   *
   * It depends on Clerk's `getToken`, which is a new closure every render, so anything
   * that lists it as a dependency is itself new every render — and `endSitting` is a
   * dependency of the effect that watches for a finished tournament, which therefore
   * re-ran on every single render. It was harmless only because `standing.current`
   * caught it.
   */
  const saveRef = useRef(saveSession);
  saveRef.current = saveSession;

  const endSitting = useCallback((g: Game, s: Setup) => {
    if (standing.current) return;
    standing.current = true;
    setRebuy(false);
    director.current?.destroy();
    g.endSession();
    void saveRef.current(g, s);
    const st = g.stats[g.config.heroSeat];
    const t = tally.current;
    /* Box 6. On the device, not in the post — `prefs.ts` carries the argument. */
    setLastSitting({
      at: Date.now(),
      cardId: seat.card?.id ?? null,
      seats: g.config.seats,
      stackBb: s.stackBb,
      format: g.config.format,
      hands: st?.hands ?? 0,
      net: st?.net ?? 0,
      biggestLostPot: t.biggestLostPot > 0 ? t.biggestLostPot : null,
      worstSpot: t.worst ? { street: t.worst.street, label: t.worst.label } : null,
    });
    setScreen("ledger");
  }, [seat.card]);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  /* Leaving the route kills every timer the Director holds. Nothing fires after. */
  useEffect(() => () => { director.current?.destroy(); director.current = null; }, []);
  useEffect(() => onPaceChange(() => setMotion(motionNow())), []);

  /*
   * ======================= PHASE 6: THE TABLE MAKES A NOISE =====================
   * Three subscriptions and one rule.
   *
   * THE RULE is that nothing constructs an `AudioContext` until a click: browsers
   * refuse otherwise and a page that tries carries a console error on every load. So
   * this effect only tells the module what the SETTINGS are; `unlock()` is called from
   * `act`, from the seat card's Deal, and from the tray — all of them gestures.
   *
   * The cues come off the same step log the flights and the voice read (`cueForStep`),
   * the payoff's win and lose come off the felt, and the pop comes off a taunt.
   */
  useEffect(() => {
    const apply = () => {
      const p = getPrefs();
      setMuted(!p.sound);
      setVolume(p.soundVolume);
    };
    apply();
    return onPrefsChange(apply);
  }, []);

  useEffect(() => flights.subscribe((step) => {
    const cue = cueForStep(step as { kind: string; action?: { kind: string }; chips?: boolean });
    if (cue) playCue(cue);
  }), [flights]);

  useEffect(() => {
    if (!voice) return;
    return voice.subscribe((e) => { if (e.t === "taunt") playCue("taunt-pop"); });
  }, [voice]);

  /*
   * Win and lose, once per payoff.
   *
   * Off the payoff rather than off a `push` step, because a push is a fact about chips
   * and the payoff is a fact about the player: a split pot pushes twice, a side pot
   * pushes twice more, and the table should say "you won" once. A hero who folded gets
   * nothing at all — losing a pot you were not in is not a loss.
   */
  const lastPayoff = useRef<Payoff | null>(null);
  useEffect(() => {
    const p = scene.payoff;
    if (!p || p === lastPayoff.current) return;
    lastPayoff.current = p;
    const heroSeat = game?.config.heroSeat ?? -1;
    if (p.winners.includes(heroSeat)) playCue("win");
    else if (p.losers.includes(heroSeat)) playCue("lose");
  }, [scene.payoff, game]);

  /*
   * And a first gesture of last resort.
   *
   * `PokerPlay` unlocks when a card is taken, which covers every ordinary path to a
   * felt. A player who pasted a seat URL, or reloaded one, has made no gesture at all,
   * so the first tap or key anywhere on this screen does it — once, then the listener
   * removes itself. This is the standard shape and it is here rather than in
   * `sounds.ts` because a module that installs window listeners on import is a module
   * that cannot be tested under Node.
   */
  useEffect(() => {
    const once = () => {
      unlockSound();
      window.removeEventListener("pointerdown", once);
      window.removeEventListener("keydown", once);
    };
    window.addEventListener("pointerdown", once);
    window.addEventListener("keydown", once);
    return () => {
      window.removeEventListener("pointerdown", once);
      window.removeEventListener("keydown", once);
    };
  }, []);

  /* The audio device goes when the whole game screen does, not on every re-mount. */
  useEffect(() => () => closeSound(), []);

  /*
   * The tray's cooldown, drawn rather than merely enforced.
   *
   * `Voice` owns the rule — it refuses a taunt inside twenty seconds whatever this
   * says — and this is only what the button shows, counted down four times a second so
   * the ring moves. A button that is dead for twenty seconds and does not say why is a
   * button players press twice and then stop trusting.
   */
  useEffect(() => {
    if (trayLeft <= 0) return;
    const h = window.setInterval(() => setTrayLeft((ms) => Math.max(0, ms - 250)), 250);
    return () => window.clearInterval(h);
  }, [trayLeft > 0]);

  /*
   * ===================== PHASE 6: THE HERO IS OFFERED *SHOW* ====================
   * Decided 7 Sep: after an uncontested win or after folding, the hand nobody made the
   * player reveal is theirs to reveal, for three seconds.
   *
   * Both cases are "the hand ended and your cards were never turned over", which is
   * exactly the case `Game.liveHoles` exists for — it returns null while a hand is
   * live, so the offer cannot appear a moment early even if this effect were wrong
   * about the timing. The offer stands for `showHold` and then goes; it does not wait
   * to be dismissed, because between hands is a place nobody should have to tap.
   * ==========================================================================
   */
  useEffect(() => {
    const g = game;
    const p = scene.payoff;
    if (!g || !p) return;
    const h = g.config.heroSeat;
    const heroFolded = snap?.state?.seats[h]?.folded ?? false;
    const wonUncontested = p.uncontested && p.winners.includes(h);
    if (!wonUncontested && !heroFolded) return;
    const cards = g.liveHoles(h);
    if (!cards || cards.length !== 2) return;
    setShowOffer(cards);
    const t = window.setTimeout(() => setShowOffer(null), motion.showHold);
    return () => window.clearTimeout(t);
    /* Keyed on the payoff object, so one hand offers once. */
  }, [scene.payoff, game]);
  useEffect(() => onPrefsChange(() => {
    setAutoDeal(getPrefs().autoDeal);
    setMotion(motionNow());
    /* The persona, the taunt switch and the sound switch all live in the same store,
       and all three are read at render. One bump re-reads every one of them. */
    setPersona(getPersona());
    setTalkOn(getPrefs().taunts);
  }), []);

  /**
   * Save on the way out, whichever way out it is.
   *
   * The Leave button was the only path that saved, and it is a small quiet control in
   * the header while "Report" sits in the main navigation — so the likely exit was the
   * one that silently discarded the session. The single row this whole browser-side
   * architecture exists to write was the least reliable part of it.
   *
   * `pagehide` rather than `beforeunload`: it is the event that actually fires on
   * mobile, and it fires on tab discard as well as on close. `saved` is the
   * idempotency guard, so all three paths can safely race.
   */
  const exitRef = useRef<{ game: Game | null; setup: Setup; save: typeof saveSession }>({
    game: null, setup, save: saveSession,
  });
  exitRef.current = { game, setup, save: saveSession };

  useEffect(() => {
    /**
     * Empty dependencies, and everything the handler needs read from a ref.
     *
     * With `[saveSession]` this ran on every render — Clerk's `getToken` is not a
     * stable identity, so the callback was new each time, and React ran the CLEANUP,
     * which is the save. That is a POST per render: the note "could not save" appeared
     * while the player was still at the table, and a failure that resets the guard so
     * it can be retried turned into a request storm.
     */
    const flush = () => {
      const { game: g, setup: s, save } = exitRef.current;
      if (g) void save(g, s);
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);

  const build = useCallback((s: Setup) => {
    /**
     * A new shuffle for every table, unless the player asked for a specific one.
     * Resolved here rather than held in the form's state, because the form's state
     * persists across sit-downs and that is how the same session got dealt twice.
     */
    const seed = seedFor(s);
    const bots = tableOf(s.seats - 1, s.difficulty);
    const profiles: string[] = [];
    let b = 0;
    for (let i = 0; i < s.seats; i++) {
      profiles.push(i === s.heroSeat ? "tag" : bots[b++]!.id);
    }
    const config: GameConfig = {
      seats: s.seats,
      heroSeat: s.heroSeat,
      botProfiles: profiles,
      rules: {
        smallBlind: s.bigBlind / 2, bigBlind: s.bigBlind,
        ante: s.ante, actionSet: "full",
        /**
         * A quarter of a blind. Fine enough that every sensible bet size is reachable,
         * coarse enough that the felt shows "22.5" rather than "22.62" — which at nine
         * seats is nine unreadable numbers instead of nine readable ones.
         *
         * ============ PHASE 6, BOX 0(2): A CASH TABLE DEALS IN WHOLE BUCKS =======
         * A quarter blind is 0.50 at a 1/2 table and 1.25 at a 2.5/5 one, and the
         * chip unit is what the ENGINE quantises every amount to — bots included, who
         * size in half big blinds. So the fractions on `phase5-table-live-phone.jpg`
         * were never only the hero's slider: at a cash table, where a chip IS a Buck
         * and a Buck does not divide, the unit itself was wrong.
         *
         * This is a `GameConfig` the CLIENT builds, so raising it to a whole Buck
         * needs nothing from `app/engine/`, and it makes the whole felt whole rather
         * than only the controls this box was asked to fix. A tournament's chips were
         * never Bucks and keep the quarter blind they always had.
         * ========================================================================
         */
        chipSize: s.format === "cash"
          ? Math.max(1, Math.round(s.bigBlind / 4))
          : s.bigBlind / 4,
      },
      startingStack: s.bigBlind * s.stackBb,
      format: s.format,
      resetStacks: s.format === "cash" && s.resetStacks,
      button: 0,
      seed,
      botTrials: s.fastBots ? 120 : 300,
    };
    try {
      const g = new Game(config);
      director.current?.destroy();
      /*
       * ==================== PHASE 6: THE TABLE GETS A VOICE ====================
       * Built here, beside the `Game`, because this is the one place that knows which
       * ARCHETYPE is in which chair: `tableOf` chose them four lines up. `Game.reveal()`
       * would answer the same question and throws until the session is finished — on
       * purpose, per the Phase 5 report's §6 — so asking it mid-hand is not an option
       * and is not needed.
       *
       * `enabled` is a function rather than a value so Table talk can be switched off
       * mid-sitting from the Settings sheet and take effect on the next decision.
       * ======================================================================
       */
      const theVoice = new Voice({
        seats: s.seats,
        hero: s.heroSeat,
        archetypes: profiles.map((id, i) => (i === s.heroSeat ? null : id as ArchetypeId)),
        seed: g.sessionCommitment,
        enabled: () => getPrefs().taunts,
        holes: (seat) => g.liveHoles(seat),
        /*
         * "A monster", for the one archetype whose rule needs the phrase.
         *
         * The Regular shows a monster and nothing else, and the only honest measure of
         * one the client has is the engine's own evaluator over the shown hand and the
         * finished board: a full house or better. `derivePayoff` uses the same
         * `evaluate7`, so there is one ranking on this felt and not two.
         */
        strength: (seat) => {
          const holes = g.liveHoles(seat);
          const board = director.current?.snapshot().state?.board ?? [];
          if (!holes || holes.length !== 2 || board.length !== 5) return null;
          return decodeScore(evaluate7([...holes, ...board])).category >= 6 ? 1 : 0;
        },
      });
      setVoice(theVoice);
      director.current = new Director({
        game: g, viewer: s.heroSeat, durations: motionNow, autoDeal: true,
        onChange: setSnap,
        onStep: (step, before) => flights.emit(step, before),
        /* Phase 6: the hurry-up's own tick, which Phase 3 left as a hook. */
        onTick: () => playCue("timer-tick"),
      });
      setSnap(director.current.snapshot());
      setGame(g);
      // The resolved seed is kept so the setup screen can show what was just played
      // and the player can note it down — but `seedLocked` decides whether it is reused.
      setSetup({ ...s, seed });
      setError(null);
      startedAt.current = Date.now();
      saved.current = false;
      standing.current = false;
      setSaveNote(null);
      /* A new sitting starts with an empty tally and an empty wallet record. */
      paid.current = 0;
      ticket.current = null;
      setSettled(null);
      tally.current = { seen: -1, biggestPot: 0, biggestLostPot: 0, worst: null, place: null };
      /*
       * Phase 3b: the table's pictures first, then the first hand — so the seats are
       * faces and the deck is a deck in the first frame, not green discs and a blank
       * card. Bounded by `preloadCeiling`; `preloadTable` never rejects.
       */
      /*
       * ============ PHASE 5: BUILT HERE, DEALT BY THE SEAT CARD ================
       * This used to preload the table's pictures and then deal the first hand. Both
       * moved to `SeatFinder`, and the reason is not tidiness: the preload was three
       * seconds of a blank screen, and the session commitment — which `FAIRNESS.md`
       * says is on screen from sit-down — was not on screen at all until the player
       * scrolled past the felt to the hand history.
       *
       * So the `Game` is CONSTRUCTED here, before the card is shown, which is what
       * gives the card a real commitment to publish for the table about to be played.
       * Nothing is dealt until the card is done with its two-to-five seconds.
       * ========================================================================
       */
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start that table.");
    }
  }, [flights]);

  const deal = useCallback((g: Game) => {
    /**
     * Refuse rather than throw when there is nothing to deal.
     *
     * The auto-deal timer and the manual button can both fire for the same hand — the
     * player clicks "Next hand" a beat before the timer — and `startHand` throws on
     * the second one. That throw was caught into an error message, but none of the
     * effect's dependencies changed on that path, so no replacement timer was ever
     * scheduled: auto-deal died for the rest of the session and the button reproduced
     * the same error every time.
     */
    if (g.phase !== "hand_over" && g.phase !== "idle") return;
    /**
     * And refuse once the player has stood up.
     *
     * `endSession()` sets `phase = "finished"`, but `finishIfNeeded` reassigns the
     * phase at the end of every hand and overwrites it. So a player who left mid-hand
     * had a live table behind the reveal panel: the hand completed, the phase went
     * back to `hand_over`, auto-deal fired, and the table went on dealing — none of it
     * recorded, because `saved` was already set. `standing` is React's own record of
     * the decision and nothing in the engine can overwrite it.
     */
    if (standing.current) return;
    try {
      /*
       * Through the Director, which refuses while the last hand is still being
       * presented — the "Next hand" button pressed during the pot push must not start
       * a deal on top of a flying pot. It also owns the auto-deal pause now.
       */
      director.current?.dealNext();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The hand could not be dealt.");
    } finally {
      bump();
    }
  }, [bump]);

  const act = useCallback((a: Action) => {
    const g = game;
    if (!g) return;
    /*
     * A PRESS, as opposed to a clock.
     *
     * `Director.expire()` acts through this same method's own path — `director.act` —
     * so nothing downstream can tell a decision from a time-out except the fact that a
     * human touched something. Voice needs to know: one of its five triggers is "the
     * hero ran out of time and somebody said so".
     */
    voice?.heroPress();
    /* And the first press of the session is what is allowed to start the audio. */
    unlockSound();
    try {
      /* The Director applies it to the engine and presents the reply at pace. */
      if (!director.current?.act(a)) return;
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That move was not legal.");
    } finally {
      bump();
    }
  }, [game, bump, voice]);

  /*
   * Auto-deal is the Director's: it counts `betweenHands` from the moment the hand is
   * PRESENTED as over, not from the moment the engine finished it, which is earlier by
   * the whole pot push. This only keeps the setting in step with the checkbox.
   */
  useEffect(() => {
    if (director.current) {
      director.current.autoDeal = autoDeal && screen === "table" && !rebuy;
    }
  }, [autoDeal, screen, rebuy, game]);

  /**
   * A tournament that ends on its own still has to be saved.
   *
   * The only save paths were the Leave button and the pagehide/unmount flush. Play a
   * tournament to the last player and the reveal appears without either firing; press
   * "Back to the lobby" and `setGame(null)` runs, so the flush's `if (g)` guard skips
   * it too. The single row the whole browser-side design exists to write was dropped
   * for precisely the sessions somebody played to the end.
   *
   * Read off the ref rather than the closure for the reason the flush effect gives:
   * `getToken` is not a stable identity, so depending on `saveSession` would re-run
   * this on every render.
   */
  useEffect(() => {
    if (game?.phase !== "finished") return;
    const e = exitRef.current;
    /* Phase 5: the same path Leave takes, so a tournament played to the last player
       gets the Ledger rather than a felt with nothing left to deal on it. */
    if (e.game) endSitting(e.game, e.setup);
  }, [game, game?.phase, endSitting]);

  /* ============================================================ PHASE 5 ===== */

  /**
   * What this sitting has shown, accumulated hand by hand.
   *
   * NOT computed by iterating `game.hands`, and the engine's own comment says why: that
   * array is a replay buffer capped at 300, so a total taken from it is a total over the
   * last three hundred hands wearing a session's name. The first benchmark run made
   * exactly that mistake and reported a VPIP of 225%.
   *
   * So each hand is folded in once, as it finishes, and `seen` is how far the fold has
   * got. Four things come out of it that `Stats` does not carry: the biggest pot at the
   * table, the biggest pot the HERO lost (which is what the Barkeep's line reads), the
   * spot the most EV went on, and — for a tournament — the place, counted at the moment
   * the hero busted rather than at the end.
   */
  const tally = useRef<{
    seen: number; biggestPot: number; biggestLostPot: number;
    worst: { street: Street; label: string; ev: number } | null;
    place: { place: number; of: number } | null;
  }>({ seen: -1, biggestPot: 0, biggestLostPot: 0, worst: null, place: null });

  useEffect(() => {
    const g = game;
    if (!g) return;
    const hero = g.config.heroSeat;
    if (hero < 0) return;
    const t = tally.current;
    for (const h of g.hands) {
      if (h.index <= t.seen) continue;
      t.seen = h.index;
      const pot = h.result.pots.reduce((a, p) => a + p.amount, 0);
      if (pot > t.biggestPot) t.biggestPot = pot;
      /* A pot the hero was IN and did not take. "In" is having put a chip in it, which
         is what `net` records — the blinds included, because a blind is money lost. */
      const won = h.result.pots.some((p) => p.winners.includes(hero));
      const wasIn = h.actions.some((a) => a.seat === hero) || (h.result.net[hero] ?? 0) !== 0;
      if (!won && wasIn && pot > t.biggestLostPot) t.biggestLostPot = pot;
      for (const a of h.actions) {
        if (a.seat !== hero || !a.graded) continue;
        if (a.graded.evLost > (t.worst?.ev ?? 0)) {
          t.worst = {
            street: a.street, ev: a.graded.evLost,
            label: spotLabel(a.street, positionOf(hero, h.button, g.config.seats)),
          };
        }
      }
      /*
       * The place, counted the moment the hero ran out of chips.
       *
       * The engine exposes no placement — see the report's engine table. What it does
       * expose is `stacks`, and a tournament finish is a countable fact about it: every
       * seat still holding chips when you bust finishes above you. Counted at the end
       * instead it would say "second" for a player who busted ninth, because by then
       * only the winner has chips.
       */
      if (g.config.format === "tournament" && !t.place && (g.stacks[hero] ?? 0) <= 0) {
        const alive = g.stacks.filter((v) => v > 0).length;
        t.place = { place: alive + 1, of: g.config.seats };
      }
    }
  }, [game, snap?.phase, snap?.state?.handNumber]);

  /**
   * The hand-end verdict: one line, when the model disagrees with what the hero did.
   *
   * Hung off `scene.payoff` on purpose. That is the same moment the payoff line appears
   * — the pause after the last reveal — and `useScene` clears it at the next shuffle,
   * so the verdict arrives and leaves with the result it is about. Nothing new times
   * anything, and `director.ts` is not opened.
   */
  const verdict: Verdict | null = useMemo(() => {
    const g = game;
    if (!g || !scene.payoff || screen !== "table") return null;
    const h = g.hands[g.hands.length - 1];
    if (!h) return null;
    return verdictLine({
      heroSeat: g.config.heroSeat,
      actions: h.actions,
      bigBlind: g.config.rules.bigBlind,
      cardId: seat.card?.id ?? null,
      seats: g.config.seats,
      stackBb: setup.stackBb,
      format: g.config.format,
    });
  }, [game, scene.payoff, screen, seat.card, setup.stackBb]);

  /* The seat card's hook reads the last one of these, so it is kept on the device. */
  const verdictSeen = useRef<string | null>(null);
  useEffect(() => {
    if (!verdict) return;
    const key = `${verdict.text}@${game?.handIndex ?? 0}`;
    if (verdictSeen.current === key) return;
    verdictSeen.current = key;
    setLastVerdict({ text: verdict.text, drill: verdict.drill, at: Date.now() });
  }, [verdict, game]);

  /**
   * Box 4: the hero has no chips, at a cash table, and the felt stops.
   *
   * `Game.startHand()` tops a busted seat back up at a cash table — that is the engine's
   * own rule and it is why a bot rebuys silently and correctly. The hero must not be
   * rebought silently: those are their Bucks. So the client catches it at hand end,
   * before the next deal, and asks.
   */
  useEffect(() => {
    const g = game;
    if (!g || screen !== "table" || standing.current) return;
    if (g.config.format !== "cash") return;
    if (snap?.phase !== "between") return;
    if ((g.stacks[g.config.heroSeat] ?? 1) > 0) return;
    setRebuy(true);
  }, [game, screen, snap?.phase]);

  /*
   * Build the table as soon as the route mounts, and again for each new sitting at it.
   *
   * `build` is stable (its only dependency is the flight bus) and the seat comes from
   * the URL, so this runs once per link followed and once per "Play again". It does NOT
   * buy anything and does not deal: the seat card does both, in that order, which is
   * what puts the commitment on screen before the money moves.
   */
  useEffect(() => {
    setScreen("seat");
    build(seat.setup);
    /* The seat's identity, not the object: a re-render must not build a second table. */
  }, [build, seat.key, sitting]);

  /**
   * ============ PHASE 6, BOX 0(3): THE HERO'S FACE IS NOT ALSO A BOT'S =========
   * The Phase 5 fixture seated *Amos Creed* at a table whose player had picked the
   * marshal as their persona: the same portrait twice, one of them the player.
   *
   * The engine deals the cast from the session seed inside its own constructor and
   * `app/engine/` is closed, so the clashing bot cannot be swapped for a different
   * PERSON. `art/casting.ts` swaps their FACE instead — the seat keeps the name the
   * engine gave it, which is what the hand history, the wire and the reveal all use,
   * and wears the next unused portrait of the same tier for the length of the sitting.
   *
   * Computed once per `Game`, and used by the felt, the seat card and the Ledger, so
   * one player cannot be two faces on three screens.
   *
   * ABOVE the `if (!game)` return, and that is not style. It was below it for one
   * build, which made the number of hooks this component runs depend on whether a
   * table existed — React error #310, a white screen, and a walk that timed out
   * waiting for a felt that was never going to render.
   * ==========================================================================
   */
  const faces = useMemo(
    () => (game ? castFaces(game.seats.map((x) => x.name), game.config.heroSeat, persona) : {}),
    [game, persona]);

  if (!game) {
    return (
      <div className="room room-table">
        <RoomHeader room={POKER} title={seat.title} backTo="/poker/play" backLabel="Poker" />
        <div className="room-felt room-felt-empty">
          <p className="muted">{error ?? "Finding you a seat…"}</p>
        </div>
      </div>
    );
  }

  const hero = game.config.heroSeat;


  /*
   * Everything on the felt comes from the PRESENTED table. The hero's legal actions
   * come from the engine, but only once the Director says the hero's turn has been
   * shown — the engine knows the hero is to act the instant the last bot acts, which
   * can be three seconds of watching before the player should be asked anything.
   */
  const state = snap?.state ?? null;
  const phase = snap?.phase ?? "idle";
  const legal = phase === "awaiting" ? game.heroOptions() : [];
  const over = phase === "between" || phase === "finished";
  /* The Ledger is up: the sitting is over and the felt is not drawn at all. */
  const sessionOver = screen === "ledger";
  const stats = game.stats[hero];
  const d = stats ? derive(stats) : null;
  const leaks: Finding[] = stats ? findings(stats) : [];
  const last = game.hands[game.hands.length - 1] ?? null;

  /* ------------------------------------------------- what the two screens need --- */

  /**
   * The price, in the same words the card on the Play tab used.
   *
   * Built from `seatCost` and `prizePool` rather than written out, so the seat card and
   * the lobby cannot disagree about what this seat costs — which they would the first
   * time a dial moved. Null for a table that costs nothing.
   */
  const seatShape = {
    format: setup.format, seats: setup.seats, bigBlind: setup.bigBlind,
    stackBb: setup.stackBb, buyin: seat.card?.buyin ?? setup.buyin,
  };
  const seatPrice = costOf(setup);
  const priceLine = seatPrice > 0 ? {
    cost: seatPrice,
    line: setup.format === "cash"
      ? <>{stakesLabel(seatShape)} · buy in for <BucksAmount n={seatPrice} /></>
      : <>Buy-in <BucksAmount n={seatPrice} /> · pool <BucksAmount n={prizePool(seatShape)} /></>,
  } : null;

  /**
   * The cast, in the engine's own seating order, with the hero's chosen face.
   *
   * SHORT names on the card, full names to the roster. "Della Halloran" under a 48px
   * portrait ellipsises to "Della Ha…" on a phone, and a seat card whose whole job is
   * to introduce the table cannot introduce it in half-names — but `avatarUrlForName`
   * keys on the full one, so both are used and neither is guessed at.
   */
  const castForCard = game.seats.map((x, i) => ({
    name: x.shortName ?? x.name,
    /* Box 0(3): the sitting's own casting, so the card, the felt and the Ledger
       introduce the same nine faces. */
    avatar: i === hero ? personaAvatar() : seatAvatar(faces, i, x.name),
    isSelf: i === hero,
  }));

  /**
   * §7's one line on the seat card.
   *
   * `hasHistory` is "has this player finished a sitting before", not "has this player
   * pressed a card" — pressing the card is what brought them here, so it is true of
   * everybody and would make the rule "nothing for a player with no history" mean
   * nothing at all.
   */
  const hook = seatHook({
    lastVerdict: getLastVerdict(),
    cardId: seat.card?.id ?? null,
    hasHistory: getLastSitting() !== null,
  });

  /** What the Ledger reads. Every number is one the sitting actually produced. */
  const t = tally.current;
  const summary: SittingSummary = {
    kind: game.config.format === "tournament" ? "poker-tournament" : "poker-cash",
    format: game.config.format,
    hands: stats?.hands ?? 0,
    net: stats?.net ?? 0,
    bb100: d?.bb100 ?? null,
    biggestPot: t.biggestPot > 0 ? t.biggestPot : null,
    evLost: stats?.evLost ?? 0,
    evLostByStreet: stats?.evLostByStreet ?? { preflop: 0, flop: 0, turn: 0, river: 0 },
    bigBlind: game.config.rules.bigBlind,
    seats: game.config.seats,
    stackBb: setup.stackBb,
    cardId: seat.card?.id ?? null,
    biggestLostPot: t.biggestLostPot > 0 ? t.biggestLostPot : null,
    worstSpot: t.worst ? { street: t.worst.street, label: t.worst.label } : null,
    /*
     * The place, or first.
     *
     * `tally` records it the moment the hero busts. A hero still holding chips when the
     * engine calls the session finished is the last player standing, which is first —
     * and there is nothing else it can be, because a tournament finishes when one seat
     * has chips left.
     */
    placement: game.config.format !== "tournament" ? null
      : t.place ?? (game.phase === "finished" && (game.stacks[hero] ?? 0) > 0
          ? { place: 1, of: game.config.seats } : null),
    /* The prize IS the settlement: what the pool paid back for the chips that came off
       the table. It is the server's number, not one worked out here. */
    prize: settled?.credited ?? null,
  };

  /**
   * The reveal, and only once the engine will give it.
   *
   * `Game.reveal()` THROWS while the session is live — deliberately, because a caller
   * reaching for the answer key mid-session has a bug and a silent empty array would
   * ship it. So it is called only on the Ledger, after `endSession()`.
   */
  const revealed: LedgerCastRow[] =
    screen === "ledger" && game.phase === "finished" ? game.reveal() : [];

  const heroSeat = state?.seats[hero] ?? null;
  const toCall = state && heroSeat ? Math.max(0, state.currentBet - heroSeat.committed) : 0;
  const lastAction = snap?.lastAction
    ? { seat: snap.lastAction.seat, label: actionLabel(snap.lastAction.action, snap.lastAction.seat === hero) }
    : null;

  /*
   * The action bar, built once and placed by the table.
   *
   * The table takes it as a child and decides itself whether that means a band under
   * the hero or an overlay in the corner. The bar shows the sizing pills without a tap
   * and gives all-in its own button, and changes nothing about which actions are legal.
   */
  const actionBar = !sessionOver && legal.length > 0 && state && heroSeat ? (
    <ActionBar
      /**
       * Identifies this decision. Changes on every new hand, street, or bet
       * facing the hero — which is exactly when the sizing controls must reset.
       */
      spotId={`${state.handNumber}:${state.street}:${state.currentBet}:${heroSeat.committed}`}
      legal={legal}
      pot={state.pot}
      toCall={toCall}
      committed={heroSeat.committed}
      stack={heroSeat.stack}
      /* Box 0(2): the bar sizes in whole units, not in quarter blinds. */
      chipSize={sizingUnit(game.config.format, game.config.rules.chipSize)}
      bigBlind={game.config.rules.bigBlind}
      busy={false}
      variant="topdown"
      onAct={act}
    />
  ) : null;

  /*
    One provider around the whole screen. The seat boxes, the pot, the action bar and
    the hand history have to switch units together — a table showing chips beside a
    history showing big blinds contradicts itself mid-hand. The unit comes from THIS
    table's blind and format, so nothing is ever guessing at a scale.

    The room header is INSIDE it, because the units toggle lives up there now and a
    control cannot write to a context it sits outside of.
  */
  return (
    /*
      A cash table's chips ARE Buster Bucks from this phase — the seat was bought with
      them and the stack is what will be settled back — so the felt is told `"bucks"`
      and never prints a `$`. A tournament's chips are chips, which is what keeps free
      entry meaning what it says; that format is passed through unchanged.
    */
    <MoneyProvider bigBlind={game.config.rules.bigBlind}
                   format={game.config.format === "cash" ? "bucks" : "tournament"}>
      <div className="room room-table">
        <RoomHeader
          room={POKER}
          backTo="/poker/play"
          backLabel="Poker"
          title={seat.title}
          subtitle={`${game.config.seats}-max · ${setup.stackBb}bb · hand ${game.handIndex + (over ? 0 : 1)}`}
          right={
            <>
              {/* Phase 7: Saloon | Top-down, on the felt only, and only where the
                  saloon fits (it draws nothing on a phone). */}
              {screen === "table" && <ViewToggle className="room-units room-view" />}
              <MoneyToggle className="room-units" />
              {/*
                Standing up ends the session, which is what unlocks the reveal. A cash
                table never finishes on its own — busted seats rebuy — so without this
                the reveal was reachable in tournaments and unreachable in cash, which
                is most of the play. Phase 5: one function does it, shared with the
                tournament's own ending and with declining a rebuy — and it is only on
                the table screen, because there is nothing to stand up from on the other
                two.
              */}
              {screen === "table" && (
                <button className="room-leave"
                        onClick={() => endSitting(game, setup)}>Leave</button>
              )}
            </>
          }
        />

        {/*
          * ================= PHASE 5: THREE SCREENS, ONE ROUTE ==================
          * Finding a seat, the felt, the Ledger. Exactly one is drawn: the seat card
          * is not an overlay on an empty table and the Ledger is not an overlay on a
          * live one, which is what the reveal panel was and what made a stood-up
          * player's table go on dealing behind it.
          * ======================================================================
          */}
        {screen === "seat" && (
          <SeatFinder
            title={seat.title}
            brief={seat.card?.line ?? null}
            price={priceLine}
            commitment={game.sessionCommitment}
            cast={castForCard}
            hook={hook}
            canStake={wallet.canStake}
            preloadNames={game.seats.map((x) => x.name)}
            buy={() => purchase(setup)}
            onGo={() => {
              /* The seat card's own cue, at `go` — the state machine's last state, and
                 the moment the player is let in. */
              playCue("seat");
              setScreen("table");
              deal(game);
            }}
            onBack={() => navigate("/poker/play")}
          />
        )}

        {screen === "ledger" && (
          <LedgerHere
            title={seat.title}
            summary={summary}
            /* Three states, and the fourth is "still going": while the post is in
               flight the screen says so rather than printing a zero it cannot stand
               behind. `paid` survives `ticket` being cleared, so it is what says
               whether there was ever money in this sitting at all. */
            money={settled ?? (paid.current > 0 ? { paid: paid.current, credited: null } : null)}
            settling={settling}

            cast={revealed}
            faces={faces}
            note={saveNote}
            onAgain={() => {
              /*
               * Retry the settlement before starting a new sitting.
               *
               * `build()` clears the ticket, and a ticket cleared before it settled is
               * a buy-in gone for good. The Ledger's own copy promises that leaving the
               * table again will retry it — and from the Ledger the only two ways out
               * are these buttons, so this is where "again" has to happen. `saveSession`
               * is idempotent on `saved`, so on a sitting that already settled this
               * costs nothing.
               */
              if (!saved.current && ticket.current) void saveSession(game, setup);
              setSitting((n) => n + 1);
            }}
            onSaloon={() => navigate("/poker/play")}
          />
        )}

        {/*
          * The felt gets everything below the header, and it is a FLEX CHILD with a
          * zero minimum — which is the whole of the `.td-embed` replacement. The old
          * box broke out of the app's 46rem column with a negative margin because the
          * table was inside a reading column it had no business being in. The room does
          * not have a reading column, so nothing has to escape one.
          */}
        <div className="room-felt" hidden={screen !== "table"}>
          {/* Toasts, absolutely placed, so an error can never move the felt. */}
          {(error || saveNote) && (
            <div className="room-toast">
              {error && <p className="err" role="alert">{error}</p>}
              {saveNote && <p className="muted small" role="status">{saveNote}</p>}
            </div>
          )}

            <Felt
              persona={persona}
              state={state}
              lastAction={lastAction}
              /*
                The table is in a room, so the room's header is the only header. Phase
                2b's review found the two stacked — the same information twice, two
                gears opening the same sheet, fifty pixels of chrome a phone cannot
                spare. The wallet gauge and the gear are up there now.
              */
              chrome="room"
              context={`${game.config.seats}-max · ${setup.format} · hand ${game.handIndex + (over ? 0 : 1)}`}
              /*
                No wallet until Phase 4. The gauge used to show a placeholder 5,000 —
                the number a new account will really start with — and the Phase 2 review
                hid it: a balance nobody has is a lie, and it was going to be on every
                screenshot until the wallet is real. `wallet` undefined draws nothing.
              */
              /*
                Phase 2b: the hero's face. The engine still calls the viewer "You" and
                still carries a null `avatarId`; this is the picture the PLAYER chose,
                resolved on the client and handed in. Null until they pick one, which
                draws the silhouette.
              */
              avatarUrl={personaAvatar()}
              faces={faces}
              /*
                * Phase 6: what the table is saying, pulling and showing.
                *
                * Table talk off empties the first two HERE rather than in `useVoice`,
                * and it is the difference between "no new bubbles" and "no bubbles":
                * `Voice` stops emitting the moment the switch flips, but a bubble that
                * was already up would have sat there for the rest of its two seconds.
                * The walk caught exactly that.
                *
                * `shown` is not table talk and stays. Turning the remarks off does not
                * un-turn-over a hand somebody has already turned over.
                */
              taunts={talkOn ? said.taunts : {}}
              expressions={talkOn ? said.expressions : {}}
              shown={said.shown}
              actions={(
                <>
                  <BandClock snap={snap} state={state} payoff={scene.payoff} />
                  {/*
                    * PHASE 6: the player's own voice, at the band's edge.
                    *
                    * Outside the action bar rather than inside it, on purpose: the bar
                    * is rebuilt on every decision (`spotId` resets the sizer) and a tray
                    * that is a child of it would close itself every time a bot acted.
                    */}
                  <TauntTray
                    enabled={talkOn && screen === "table"}
                    cooldownLeft={trayLeft}
                    cooldownTotal={COOLDOWN.hero}
                    onTaunt={(id) => {
                      unlockSound();
                      const sent = voice?.heroTaunt(id) ?? false;
                      if (sent) setTrayLeft(COOLDOWN.hero);
                      return sent;
                    }} />
                  <ShowControl cards={showOffer} done={!showOffer}
                               onShow={(cards) => { voice?.heroShow(cards); setShowOffer(null); }} />
                  {/*
                    * ================ THE BAND, AND WHY IT NEVER CHANGES HEIGHT =======
                    * `GhostActionBar` exists so that the band — and therefore the felt
                    * above it — is the same height whether or not it is the player's
                    * turn. Two things used to break that, and the Phase 5 walk measured
                    * both: between hands the ghost was REPLACED by a "Next hand" button
                    * in a much shorter box, so the band went 141px to 84px and the felt
                    * grew by fourteen pixels and moved down by twenty-two — on every
                    * single hand. And that button was drawn whether or not auto-deal was
                    * on, so a table dealing by itself flashed a control for a moment
                    * between hands.
                    *
                    * So: the ghost stays put whenever there is no real action bar, and
                    * everything that belongs between hands — the verdict, and the "Next
                    * hand" button when the player has asked to deal them one at a time —
                    * is laid OVER it. Same band, same height, nothing moves, and Phase
                    * 5's own line costs the felt nothing.
                    * ==================================================================
                    */}
                  {actionBar ?? (!sessionOver && state ? (
                    <GhostActionBar bigBlind={game.config.rules.bigBlind}
                                    chipSize={sizingUnit(game.config.format, game.config.rules.chipSize)} />
                  ) : null)}
                  {!actionBar && !rebuy && (verdict || (over && !sessionOver && !autoDeal)) && (
                    <div className="td-bandover">
                      {/* §7: one line, one tap, about the hand that just ended. */}
                      {verdict && (
                        <p className="td-verdict" role="status">
                          <span>{verdict.text}</span>
                          <Link to={`/poker/train/run?card=${verdict.drill}`}>Drill it</Link>
                        </p>
                      )}
                      {over && !sessionOver && !autoDeal && (
                        <button className="btn td-between-btn" onClick={() => deal(game)}>Next hand</button>
                      )}
                    </div>
                  )}
                </>
              )}
              dealt={snap?.dealt}
              flights={flights}
              motion={motion}
              timers={snap?.clocks}
              /*
                Phase 2b: the ⋯ opens the Settings sheet rather than a popover of its
                own. The pace picker that used to live in it is one line in the sheet
                now, beside the seven other things a player can decide.
              */
              onSettings={() => setSheetOpen(true)}
              deck={scene.deck}
              payoff={scene.payoff}
              potFlying={scene.potFlying}
              potFrozen={scene.potFrozen}
            />

          {/*
            * ================= BOX 4: BUSTED AT A CASH TABLE ====================
            * The felt is paused behind this — `autoDeal` is off while it is up — and
            * the player is ASKED rather than quietly rebought with their own Bucks.
            * A busted BOT is topped back up by the engine's own cash rule, silently and
            * correctly, because a bot's chips are not anybody's wallet.
            *
            * WHICH CARD this draws depends on `REBUY_TAKES_A_SECOND_BUYIN`, and the
            * whole argument is written out beside that constant in `lib/wallet-client.ts`.
            * In one sentence: the Worker prices a poker sitting off ONE seat's stack
            * while the ticket's buy-in is cumulative, so a second buy-in settles wrongly
            * in both directions, and the one-line fix is in a file this phase may not
            * open.
            *
            * So while it is off, a busted player is sent to the Ledger — where "Play
            * again" is a NEW sitting with its own receipt, which the wallet prices
            * exactly. No control is drawn that would mishandle their money, and none is
            * drawn that does not work. Both cards are in the Phase 5 screenshots.
            * ====================================================================
            */}
          {rebuy && (
            <div className="barkeep-scrim" role="presentation">
              <section className="barkeep" role="dialog" aria-modal="true" aria-label="Rebuy">
                {REBUY_TAKES_A_SECOND_BUYIN ? (
                  <>
                    <p className="barkeep-line">
                      You're out of chips. Another <b className="num">{costOf(setup).toLocaleString("en-US")}</b>{" "}
                      buys you back in at the same table.
                    </p>
                    {rebuyShort ? (
                      <>
                        <p className="barkeep-note">
                          You have <b className="num">{rebuyShort.balance.toLocaleString("en-US")}</b> Bucks.
                        </p>
                        {wallet.canStake
                          ? <button className="btn barkeep-go" type="button" onClick={() => setBarkeep(true)}>Ask the Barkeep</button>
                          : null}
                      </>
                    ) : (
                      <button className="btn barkeep-go" type="button" onClick={() => {
                        void purchase(setup, { rebuy: true }).then((r) => {
                          if (!r.ok) { setRebuyShort({ cost: r.cost, balance: r.balance }); return; }
                          setRebuyShort(null);
                          setRebuy(false);
                          /* The engine tops a busted cash seat back up on the next deal —
                             its own rule. What this bought is the right to that top-up. */
                          deal(game);
                        });
                      }}>Buy back in</button>
                    )}
                    <button className="btn-quiet barkeep-go" type="button"
                            onClick={() => endSitting(game, setup)}>
                      Stand up
                    </button>
                  </>
                ) : (
                  <>
                    <p className="barkeep-line">
                      You're out of chips. That is the sitting — here is how it went.
                    </p>
                    <button className="btn barkeep-go" type="button"
                            onClick={() => endSitting(game, setup)}>
                      See the Ledger
                    </button>
                  </>
                )}
                <p className="barkeep-note">
                  Buster Bucks are practice money. They refill. There is nothing to buy.
                </p>
              </section>
            </div>
          )}
        </div>
      </div>

      {/*
        * Below the fold, on purpose.
        *
        * Stats left the tab bar for the profile (master plan §4), and the table screen
        * is the table. These two are not lost and not moved off the page — they are
        * under it, where a player who wants to read their session goes to look, and
        * where they cost the felt nothing. `HandHistory` also carries the per-hand
        * shuffle verification, which is why it stays on this screen at all.
        */}
      {/*
        * Below the fold, and NOT on the seat card.
        *
        * `hidden` was tried and does not work here: the attribute's `display: none`
        * comes from the user-agent stylesheet, and `.room-extras` sets `display: grid`
        * in `rooms.css`, which wins. So the stats panel and the hand history were
        * rendering under a seat card that had not bought a seat yet. It is a render
        * condition instead — which is also honest, because there is no history to show
        * before the first hand.
        *
        * They DO stay under the Ledger. `HandHistory` carries the session proof, and
        * the session proof is at its most useful at exactly the moment the sitting has
        * ended and the seed is revealed.
        */}
      {screen !== "seat" && (
      <div className="room-extras">
        <StatsPanel stats={stats ?? null} derived={d} leaks={leaks} />
        <HandHistory game={game} />
      </div>
      )}

      {sheetOpen && <SettingsSheet onClose={() => setSheetOpen(false)} />}
      {/* Closing the Barkeep clears the refusal as well as the dialog. Without that,
          a player who took the stake was left looking at a card whose only remaining
          control was "Stand up" — staked, and unable to spend it. */}
      {barkeep && <Barkeep onClose={() => { setBarkeep(false); setRebuyShort(null); }} />}
    </MoneyProvider>
  );
}

/**
 * The Ledger, inside the money provider.
 *
 * `Ledger` takes its formatter as a prop rather than reading the context itself, so it
 * can be rendered by blackjack and by the harness with no `MoneyProvider` above it —
 * both of which have their own idea of what a chip is worth. This four-line wrapper is
 * the one place that connects the two on the poker route.
 *
 * ------------------------------------------------------------------------------
 * It replaces `Reveal`, the panel this route used to lay OVER the felt. Everything
 * that panel said is still said, on `Ledger`: the cast, the archetypes, the profile's
 * own blurb, `chartAdherence` and the sentence explaining what that percentage is and
 * is not. What has gone is the overlay — the sitting is over, so the table is not drawn
 * behind it — and what is new is the net, the row, the placement and the one drill.
 */
function LedgerHere(props: Omit<LedgerProps, "fmt">) {
  const { fmt } = useMoney();
  return <Ledger {...props} fmt={fmt} />;
}

/**
 * The clock in the action band.
 *
 * "Waiting for Della… 3 s" while a bot thinks; the hero's own countdown once it is
 * their turn, oxblood in the hurry-up. This is what replaced "Waiting for the other
 * players…" under the table: the band is where the player is already looking.
 */
function BandClock({ snap, state, payoff }: { snap: DirectorSnapshot | null; state: TableState | null; payoff: Payoff | null }) {
  const { fmt } = useMoney();
  if (!snap || !state) return null;
  /* The payoff: "You win $4,850 with three eights". Holds from the last reveal to the next shuffle. */
  if (payoff && (snap.phase === "playing" || snap.phase === "between")) {
    const name = (seat: number) => (state.seats[seat]?.isSelf ? "You" : state.seats[seat]?.identity.shortName ?? "…");
    return <div className="td-clock td-clock-win" role="status"><span>{payoffLine(payoff, name, fmt)}</span></div>;
  }
  if (snap.waiting) {
    const who = state.seats[snap.waiting.seat]?.identity.shortName ?? "…";
    return (
      <div className="td-clock" role="status">
        <span>Waiting for {who}…</span>
        <span className="num">{snap.waiting.secondsLeft} s</span>
      </div>
    );
  }
  if (snap.heroClock) {
    return (
      <div className={"td-clock" + (snap.heroClock.hurry ? " td-clock-hurry" : "")} role="status">
        <span>{snap.heroClock.hurry ? "Hurry up" : "Your move"}</span>
        <span className="num">{snap.heroClock.secondsLeft} s</span>
      </div>
    );
  }
  /*
   * Nobody's clock is running: the last thing that happened, so the band never blinks
   * empty between one seat's clock and the next — an empty band drops its leather.
   */
  if (snap.lastAction && snap.phase === "playing") {
    const who = state.seats[snap.lastAction.seat];
    return (
      <div className="td-clock td-clock-quiet" role="status">
        <span>{who?.isSelf ? "You" : who?.identity.shortName} {actionLabel(snap.lastAction.action, !!who?.isSelf)}</span>
      </div>
    );
  }
  /* The deal, and the pause after a hand: still one line, so the band's height — and
     with it the felt's position — is the same from the shuffle to the next shuffle. */
  if (snap.phase === "playing") {
    return <div className="td-clock td-clock-quiet" role="status"><span>Dealing…</span></div>;
  }
  if (snap.phase === "between") {
    return <div className="td-clock td-clock-quiet" role="status"><span>Hand over</span></div>;
  }
  return null;
}




